"use client";

/**
 * Container component for kit content overview.
 * Handles fetching required data & passing down to presentational components.
 *
 * TODO: poll for changes to current section.
 * TODO: render modified banner if section has changed
 */

import React, { useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import Head from "next/head";
import {
  buildPath,
  Flex,
  Box,
  Button,
  ItemType,
  elementIds,
  Kit,
  KitVersion,
  Section,
  Item,
  useNavigation,
  EmptyState,
  useIsClient,
} from "@thenounproject/lingo-core";

import { buildItemUrl } from "@redux/navPoint/buildItemUrl";
import { NavPointTypes } from "@redux/navPoint/NavPoint";

import MobileSubNav from "../navigation/MobileSubNav";

import SectionComponent from "./Section";
import GalleryComponent from "./DynamicGallery";
import ManualGalleryComponent from "./ManualGallery";
import ContentContainer from "../../../app/_components/ContentContainer";
import InspectorView from "../inspector/InspectorView";
import { InspectorSource } from "@constants/Inspector";
import InsertPanel from "./InsertPanel";

import { NavPoint } from "@redux/navPoint/NavPoint.js";
import useShowModal, { ModalTypes } from "@redux/actions/useModals";
import { useGetModals } from "@selectors/getters";

import { TabletDown } from "@features/media-queries";
import useContentTypes from "@actions/useFetchContentTypes";
import useKitSelection from "@components/hooks/useKitSelection";
import useConfigureMode from "../hooks/useConfigureMode";
import { useSearchParams } from "next/navigation";
import useModals from "@redux/actions/useModals";
import useNavPoint from "@src/@hooks/useNavPoint";
import useActiveSection from "@components/hooks/useActiveSection";

export const ScrollContainer = styled(Box).attrs({
  id: elementIds.SCROLL_CONTAINER,
  width: "100%",
  height: "100%",
})`
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`;

type Props = {
  kit: Kit;
  section?: Section;
  kitVersion?: KitVersion;
  versionNum: number;
  navPoint: NavPoint;
};

export enum GalleryContext {
  kit = "kit",
  deleted = "deleted",
  recovered = "recovered",
}

export enum PageContentTypes {
  section = "section",
  gallery = "gallery",
  empty = "empty",
  recoveredAssets = "recoveredAssets",
  trashedItems = "trashedItems",
}

const KitComponent: React.FC<Props> = ({
  kit,
  kitVersion,
  versionNum,
  section: _section,
  navPoint,
}) => {
  const navigate = useNavigation();
  const { showModal } = useShowModal();

  const { space, portal } = useNavPoint();
  const { configureMode } = useConfigureMode();
  const isClient = useIsClient();

  const displaySection = useActiveSection();

  const pageContentType = useMemo(() => {
    if (navPoint.type === NavPointTypes.Gallery) return PageContentTypes.gallery;
    if (navPoint.type === NavPointTypes.Item && navPoint.gallery) return PageContentTypes.gallery;
    return displaySection ? PageContentTypes.section : PageContentTypes.empty;
  }, [displaySection, navPoint.gallery, navPoint.type]);

  // MARK : Selection (via hook)
  // -------------------------------------------------------------------------------
  const displayKey = `${displaySection?.id}-${pageContentType}`;
  const {
    selectedItems,
    setSelectedItems,
    onSelectItem,
    inspectables,
    scrollContainerRef,
    disableTextSelection,
    canEditContent,
  } = useKitSelection({
    kit,
    versionNum,
    displayKey,
  });

  // Pre-load acceptable file types for direct drag and drop
  useContentTypes(
    {
      itemType: ItemType.asset,
    },
    { skip: !canEditContent }
  );

  // MARK : Watch for kit deletion
  // -------------------------------------------------------------------------------
  useEffect(() => {
    if (kit && kit.status === "deleted") {
      navigate.push(buildPath("/", { space: { id: space.id } }));
    }
  }, [navigate, kit, space.id]);

  // Scroll to top when changing sections
  useEffect(() => {
    const scrollContainer = document.getElementById("inspector-view-wrapper");
    try {
      window.scrollTo(0, 0);
      scrollContainer.scrollTo(0, 0);
    } catch {
      // If this fails ¯\_(ツ)_/¯
    }
  }, [displayKey]);

  // Adding new sections
  const openNewSectionModal = useCallback(() => {
    showModal(ModalTypes.CREATE_SECTION, { kitId: kit.kitId, space });
  }, [showModal, kit.kitId, space]);

  const openShareModal = useCallback(() => {
    if (portal) {
      showModal(ModalTypes.SHARE_PORTAL, { portalId: portal.id, kitId: kit.kitId });
    } else {
      showModal(ModalTypes.SHARE_KIT, { kitId: kit.kitId });
    }
  }, [showModal, portal, kit.kitId]);

  useOpenKitModalFromUrl(kit);

  const getItemUrl = useCallback(
    (item: Item) => {
      return buildItemUrl(item, {
        space,
        portal,
        section: displaySection,
      });
    },
    [displaySection, portal, space]
  );

  if (!isClient) {
    return null; // Don't render on server
  }

  const shouldRenderInsertPanel =
    !configureMode && pageContentType === PageContentTypes.section && canEditContent;

  // MARK : Rendering
  /* -------------------------------------------------------------------------------*/

  function renderInsertPanel() {
    if (!shouldRenderInsertPanel) return;
    return <InsertPanel emptySection={!displaySection?.counts.items} />;
  }

  function renderSection() {
    return (
      <SectionComponent
        section={displaySection}
        kit={kit}
        kitVersion={kitVersion}
        selectedItems={selectedItems}
        onSelectItem={onSelectItem}
        setSelectedItems={setSelectedItems}
        scrollContainerRef={scrollContainerRef}
        canEditContent={canEditContent}
        getItemUrl={getItemUrl}
      />
    );
  }

  function renderEmpty() {
    return canEditContent ? (
      <EmptyState
        title="Add a Page"
        subtitle="Pages keep things organized (e.g. Logos, Colors, Icons)."
        iconProps={{ iconId: "plus", size: "60", fill: "gray" }}
        extra={<Button text="Add Page" onClick={openNewSectionModal} mt="l" />}
      />
    ) : (
      <EmptyState
        title="This kit is empty"
        subtitle="No assets have been added yet."
        iconProps={{
          iconId: "empty-state.assets",
          size: "90",
          fill: "gray",
        }}
      />
    );
  }

  function renderGallery() {
    // If we are rendering the gallery behind an item detail
    // The gallery will be stored in navPoint.gallery
    // Otherwise, we are looking at the gallery itself.
    const galleryItem = navPoint.gallery ?? navPoint.item;
    if (galleryItem.data?.viewId) {
      return (
        <GalleryComponent
          section={displaySection}
          item={galleryItem}
          kit={kit}
          kitVersion={kitVersion}
          onSelectItem={onSelectItem}
          setSelectedItems={setSelectedItems}
          selectedItems={selectedItems}
          scrollContainerRef={scrollContainerRef}
          canEditContent={canEditContent}
          getItemUrl={getItemUrl}
        />
      );
    }
    return (
      <ManualGalleryComponent
        section={displaySection}
        kit={kit}
        kitVersion={kitVersion}
        onSelectItem={onSelectItem}
        setSelectedItems={setSelectedItems}
        selectedItems={selectedItems}
        scrollContainerRef={scrollContainerRef}
        canEditContent={canEditContent}
        getItemUrl={getItemUrl}
      />
    );
  }

  function renderMobileSubNav() {
    return (
      <TabletDown>
        <MobileSubNav
          button={
            kit?.access?.permissions?.includes("change_own_status") ? (
              <Button text="Share Kit" onClick={openShareModal} />
            ) : undefined
          }
        />
      </TabletDown>
    );
  }

  function renderContent() {
    function content() {
      switch (pageContentType) {
        case PageContentTypes.empty:
          return renderEmpty();
        case PageContentTypes.section:
          return renderSection();
        case PageContentTypes.gallery:
          return renderGallery();
      }
    }
    return (
      <ContentContainer
        ref={scrollContainerRef}
        poweredBy={kit.access.isPublic}
        disableTextSelection={disableTextSelection}
        styleOverrides={{ pt: "xl" }}>
        <Head>
          <title>{kit.name}</title>
        </Head>
        {content()}
      </ContentContainer>
    );
  }

  function renderKitPage() {
    return (
      <>
        <Flex data-test="kit-content-wrapper" overflow="auto" height="100%">
          <Flex width="100%" position="relative">
            {renderInsertPanel()}
            <ScrollContainer
              data-testid="scrollContainer"
              data-inspector-clear="true"
              pl={shouldRenderInsertPanel && "24px"}
              variations={{ "mq.s": { pl: 0 } }}
              data-scrollable="true">
              {configureMode ? (
                <>
                  {renderMobileSubNav()}
                  {renderContent()}
                </>
              ) : (
                <InspectorView
                  source={InspectorSource.kit}
                  inspectables={inspectables}
                  canEdit={canEditContent}>
                  {renderMobileSubNav()}
                  {renderContent()}
                </InspectorView>
              )}
            </ScrollContainer>
          </Flex>
        </Flex>
      </>
    );
  }

  return (
    <Flex flexDirection="column" width="100%" height="100%" overflow="hidden">
      {renderKitPage()}
    </Flex>
  );
};

export default KitComponent;

// MARK : Hooks
// -------------------------------------------------------------------------------
// Extracts a few hooks used in the Kit component

function useOpenKitModalFromUrl(kit: Kit) {
  const searchParams = useSearchParams();
  const { showModal } = useModals();
  const modals = useGetModals();
  useEffect(() => {
    if (modals.length) return;

    const modalName = () => {
      if (searchParams.has("modal")) {
        return searchParams.get("modal");
      }
      if (searchParams.has("invite")) {
        return "share";
      }
      return null;
    };

    switch (modalName()) {
      case "manageShareLinks":
      case "share":
        if (kit.access.permissions.includes("change_member_role")) {
          showModal(ModalTypes.SHARE_KIT, { kitId: kit.kitId });
        }
        break;
      case "release":
        if (kit.access.permissions.includes("manage")) {
          showModal(ModalTypes.CREATE_KIT_VERSION, {
            kitId: kit.kitId,
            spaceId: kit.spaceId,
          });
        }
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
